import { useEffect, useState } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useStoreContext } from 'features/store';

import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { selectors, useAppSelector } from 'state/global-state';
import { useServiceModeContext } from 'state/service-mode';

export interface IHydrationStatus {
  processed: boolean;
  executing: boolean;
  error: boolean;
}

interface IUseHydrationParams {
  setShouldSkipPriceOrder?: (value: boolean) => void;
}

export const useHydration = ({ setShouldSkipPriceOrder }: IUseHydrationParams = {}) => {
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const [hydrationStatus, setHydrationStatus] = useState<IHydrationStatus>({
    processed: false,
    executing: false,
    error: false,
  });

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { hydrateMinimalCart } = useMinimalCart();
  const { store } = useStoreContext();
  const { serviceMode } = useServiceModeContext();

  useEffect(() => {
    const hasPrerequisites = !!store?.number && !!serviceMode;
    const shouldHydrate =
      isUnifiedMenuEnabled && minimalCartEntries.length && !hydrationStatus.processed;

    if (!shouldHydrate) {
      if (!hydrationStatus.processed) {
        setHydrationStatus(status => ({ ...status, processed: true }));
      }
      return;
    }

    if (!hasPrerequisites) {
      // Wait until store/service mode are available before attempting hydration
      return;
    }

    setHydrationStatus(status => ({ ...status, processed: true, executing: true }));
    hydrateMinimalCart()
      .then((success: boolean) => {
        if (!success) {
          setShouldSkipPriceOrder?.(false);
        }
        setHydrationStatus(status => ({
          ...status,
          executing: false,
          error: !success,
        }));
      })
      .catch(() => {
        // Ensure we always clear the executing flag even if hydration throws
        setHydrationStatus(status => ({ ...status, executing: false, error: true }));
        setShouldSkipPriceOrder?.(false);
      });
  }, [
    hydrateMinimalCart,
    hydrationStatus.processed,
    isUnifiedMenuEnabled,
    minimalCartEntries.length,
    serviceMode,
    setShouldSkipPriceOrder,
    store?.number,
  ]);

  return { hydrationStatus };
};
